import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import JWyattBadge from "../../assets/JWyatt Badge.png";
import KevinHinerHeadshot from "../../assets/kevin-hiner-headshot.png";
import ShieldFooterIMG from "../../assets/shield.png";
import { supabase } from "../../utils/supabaseClient";
import { trackEventPageEvent, trackEventPageView } from "../../utils/eventPageAnalytics";
import { getCtaLinkProps, getSortedEnabledCtas } from "../../utils/eventPageCtaUtils";
import { mergeAttributionIntoPayload } from "../../utils/attributionPersistence";
import { findCalendarSlotById } from "../../utils/calendarEventUtils";
import {
  RSVP_EMAIL_VALIDATION_MESSAGE,
  validateRsvpEmail,
} from "../../utils/reserveRsvpEmailValidation";
import { PUBLIC_EVENT_PAGE_SELECT, PUBLIC_EVENT_SELECT } from "./publicEventQueries";
import AddToCalendar from "./AddToCalendar";
import "./dynamic-event-page.css";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const STATUS_OPTIONS = [
  "Veteran",
  "Active Duty",
  "Guard/Reserve",
  "Family Member/Caregiver",
  "Provider/Community Partner",
  "Other",
];
const DEFAULT_STATUS = STATUS_OPTIONS[0];
const SERVICE_STATUSES = new Set(["Veteran", "Active Duty", "Guard/Reserve"]);
const BRANCH_OPTIONS = [
  "Army",
  "Army National Guard",
  "Navy",
  "Marine Corps",
  "Air Force",
  "Coast Guard",
  "Space Force",
];
const ERA_OPTIONS = ["Pre-9/11", "Post-9/11"];
const DEFAULT_RSVP_CONFIG = {
  attendee_mode: "single",
  max_party_size: 1,
  show_party_size: true,
  default_status: DEFAULT_STATUS,
  show_phone: true,
  require_phone: false,
  show_branch: true,
  require_branch: false,
  show_era: true,
  require_era: false,
  show_address: false,
  require_address: false,
  mailing_list_opt_in_default_checked: true,
  show_peer_contact_opt_in: false,
  show_rhp_client_status: true,
};
const DEFAULT_IMAGE_CONFIG = {
  hero_image_url: "",
  banner_image_url: "",
  og_image_url: "",
  show_banner_image: false,
};
const DEFAULT_THANK_YOU_CONFIG = {
  title: "",
  body: "",
  primary_cta_label: "",
  primary_cta_url: "",
  secondary_cta_label: "",
  secondary_cta_url: "",
};
const DEFAULT_SUNSET_CONFIG = {
  enabled: false,
  hours_after_event_end: 24,
  title: "",
  message: "",
  cta_label: "",
  cta_url: "",
  hide_event_content: false,
};
const LOCAL_HERO_IMAGES_BY_SLUG = {
  "service-member-ethics-webinar-2026": KevinHinerHeadshot,
  "unified-protocol-level-1-workshop-2026": JWyattBadge,
};

function firstDefined(obj, keys) {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

function coerceString(value) {
  if (value === undefined || value === null) return "";
  return String(value).trim();
}

function normalizeCompactTimeZoneText(value) {
  return coerceString(value).replace(/\bC(?:D|S)T\b/g, "CT");
}

function firstImageUrlExcept(excludedUrl, candidates) {
  const excluded = coerceString(excludedUrl);
  for (const candidate of candidates) {
    const value = coerceString(candidate);
    if (value && value !== excluded) return value;
  }
  return "";
}

function titleCaseFromSlug(slug = "") {
  return slug
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function formatDateTime(dateUtc, timezone) {
  if (!dateUtc) return "";

  const date = new Date(dateUtc);
  if (Number.isNaN(date.getTime())) return "";

  const tz = timezone || "UTC";

  try {
    const formatted = new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      timeZone: tz,
      timeZoneName: "short",
    }).format(date);
    return normalizeCompactTimeZoneText(formatted);
  } catch {
    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(date);
  }
}

function formatSlotLabel(slot, eventTimezone) {
  const explicitLabel = normalizeCompactTimeZoneText(firstDefined(slot, ["label", "name", "slot_label"]));
  const startUtc = firstDefined(slot, ["start_utc", "starts_at", "start_at"]);
  const endUtc = firstDefined(slot, ["end_utc", "ends_at", "end_at"]);

  if (!startUtc) return explicitLabel || "RSVP slot";

  const start = new Date(startUtc);
  const end = endUtc ? new Date(endUtc) : null;
  if (Number.isNaN(start.getTime())) return explicitLabel || "RSVP slot";

  const tz = eventTimezone || "UTC";
  const timeFormatter = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: tz,
  });

  const timeRange = end && !Number.isNaN(end.getTime())
    ? `${timeFormatter.format(start)} - ${timeFormatter.format(end)}`
    : timeFormatter.format(start);

  if (!explicitLabel) return timeRange;

  const normalizedLabel = explicitLabel.replace(/\s+/g, " ");
  const normalizedTimeRange = timeRange.replace(/\s+/g, " ");
  if (normalizedLabel.includes(normalizedTimeRange)) return explicitLabel;

  return `${explicitLabel} • ${timeRange}`;
}

function getConfiguredDefaultStatus(value) {
  const normalized = coerceString(value);
  return STATUS_OPTIONS.includes(normalized) ? normalized : DEFAULT_STATUS;
}

function renderBodyMarkdown(body) {
  const lines = coerceString(body).split(/\r?\n/);
  const blocks = [];
  let listItems = [];
  let paragraphLines = [];

  function flushList() {
    if (!listItems.length) return;
    blocks.push(
      <ul key={`list-${blocks.length}`} className="dynamic-event-page__list">
        {listItems.map((item, index) => (
          <li key={`${item}-${index}`}>{item}</li>
        ))}
      </ul>
    );
    listItems = [];
  }

  function flushParagraph() {
    if (!paragraphLines.length) return;
    blocks.push(
      <p key={`p-${blocks.length}`} className="dynamic-event-page__paragraph">
        {paragraphLines.join(" ")}
      </p>
    );
    paragraphLines = [];
  }

  for (const rawLine of lines) {
    const line = rawLine.trim();

    if (!line) {
      flushList();
      flushParagraph();
      continue;
    }

    const headingMatch = line.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      flushList();
      flushParagraph();
      const level = headingMatch[1].length;
      const text = headingMatch[2].trim();
      if (level === 1) {
        blocks.push(
          <h2 key={`h-${blocks.length}`} className="dynamic-event-page__body-heading">
            {text}
          </h2>
        );
      } else {
        blocks.push(
          <h3 key={`h-${blocks.length}`} className="dynamic-event-page__body-subheading">
            {text}
          </h3>
        );
      }
      continue;
    }

    const listMatch = line.match(/^[-*+]\s+(.+)$/);
    if (listMatch) {
      flushParagraph();
      listItems.push(listMatch[1].trim());
      continue;
    }

    paragraphLines.push(line);
  }

  flushList();
  flushParagraph();

  return blocks;
}

function RHPSiteFooter() {
  return (
    <footer className="dynamic-event-page-footer tdp-footer" role="contentinfo">
      <div className="tdp-footer__inner">
        <div className="tdp-footer__brand">
          <img
            src={ShieldFooterIMG}
            alt=""
            aria-hidden="true"
            className="tdp-footer__shield"
            decoding="async"
          />
          <div className="tdp-footer__brandtext">
            <div className="tdp-footer__title">Road Home Program</div>
            <div className="tdp-footer__org">
              The National Center of Excellence for Veterans and Their Families at Rush
            </div>
            <div className="tdp-footer__contact">
              1645 W. Jackson Blvd., Suite 602, Chicago, IL 60612
              <span className="tdp-dot" aria-hidden>
                •
              </span>
              <a href="tel:13129428387">(312) 942-8387 (VETS)</a>
            </div>
          </div>
        </div>

        <nav className="tdp-footer__nav" aria-label="Footer">
          <a href="https://roadhomeprogram.org/family-center/">Help for Families</a>
          <a href="https://roadhomeprogram.org/get-care/">Get Care</a>
          <a href="https://roadhomeprogram.org/accelerated-treatment-program/">
            Accelerated Treatment Program
          </a>
          <a href="https://roadhomeprogram.org/outpatient-program/">Outpatient Program</a>
          <a href="https://roadhomeprogram.org/outreach-and-events/">Outreach and Events</a>
          <a href="https://roadhomeprogram.org/contact-us/">General Information</a>
          <a
            href="https://www.rush.edu/website-privacy-statement"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Statement
          </a>
          <a href="https://www.rush.edu/disclaimer" target="_blank" rel="noreferrer">
            Disclaimer
          </a>
          <a
            href="https://www.rush.edu/sites/default/files/rush-nondiscrimination-policy.pdf"
            target="_blank"
            rel="noreferrer"
          >
            Nondiscrimination Policy
          </a>
        </nav>

        <div className="tdp-footer__legal">© {new Date().getFullYear()} Road Home Program at Rush.</div>
      </div>
    </footer>
  );
}

function normalizeSlots(rows, eventTimezone) {
  return (rows || [])
    .filter((slot) => slot && firstDefined(slot, ["id"]))
    .map((slot) => ({
      id: firstDefined(slot, ["id"]),
      event_id: firstDefined(slot, ["event_id"]),
      label: formatSlotLabel(slot, eventTimezone),
      start_utc: firstDefined(slot, ["start_utc", "starts_at", "start_at"]),
      end_utc: firstDefined(slot, ["end_utc", "ends_at", "end_at"]),
    }));
}

function parseEventDate(value) {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function resolveEventEndDate(eventRecord, slots) {
  const explicitEnd = firstDefined(eventRecord, ["end_date_utc", "end_utc", "ends_at", "end_at"]);
  const parsedExplicitEnd = parseEventDate(explicitEnd);
  if (parsedExplicitEnd) return parsedExplicitEnd;

  const slotEndDates = (slots || [])
    .map((slot) => parseEventDate(firstDefined(slot, ["end_utc", "start_utc"])))
    .filter(Boolean);

  if (slotEndDates.length) {
    return new Date(Math.max(...slotEndDates.map((date) => date.getTime())));
  }

  return parseEventDate(firstDefined(eventRecord, ["date_utc", "start_utc", "starts_at", "start_at"]));
}

function buildMapsUrl(address) {
  const value = coerceString(address);
  return value ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(value)}` : "";
}

function getCurrentPublicEventUrl() {
  if (typeof window === "undefined") return "";
  return `${window.location.origin}${window.location.pathname}`;
}

function renderCtaLink(cta, className, onClick) {
  const content = (
    <>
      <span className="dynamic-event-page__cta-block-label">{cta.label}</span>
      {cta.helper_text ? (
        <span className="dynamic-event-page__cta-block-helper">{cta.helper_text}</span>
      ) : null}
    </>
  );

  if (cta.url.startsWith("/")) {
    return (
      <Link className={className} to={cta.url} onClick={() => onClick?.(cta)}>
        {content}
      </Link>
    );
  }

  return (
    <a className={className} href={cta.url} onClick={() => onClick?.(cta)} {...getCtaLinkProps(cta.url)}>
      {content}
    </a>
  );
}

function renderCtaGroup(ctas, placement, options = {}) {
  if (!ctas.length) return null;

  const sectionClassName = [
    "dynamic-event-page__cta-group",
    `dynamic-event-page__cta-group--${placement}`,
    options.className,
  ].filter(Boolean).join(" ");

  return (
    <section className={sectionClassName}>
      {options.title ? <h2>{options.title}</h2> : null}
      {options.description ? <p>{options.description}</p> : null}
      <div className="dynamic-event-page__cta-blocks">
        {ctas.map((cta) => (
          <React.Fragment key={cta.id}>
            {renderCtaLink(
              cta,
              `dynamic-event-page__cta-block dynamic-event-page__cta-block--${cta.style}`,
              options.onCtaClick
            )}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
}

function formatPhoneDigits(digits) {
  const a = digits.slice(0, 3);
  const b = digits.slice(3, 6);
  const c = digits.slice(6, 10);
  if (digits.length <= 3) return a ? `(${a}` : "";
  if (digits.length <= 6) return `(${a}) ${b}`;
  return `(${a}) ${b}-${c}`;
}

function extractResponseFromError(errorObj) {
  if (!errorObj?.context || typeof errorObj.context.json !== "function") {
    return Promise.resolve(null);
  }
  return errorObj.context.json().catch(() => null);
}

function DynamicEventPage() {
  const { slug = "" } = useParams();
  const [state, setState] = useState({
    loading: true,
    error: "",
    notFound: false,
    page: null,
    event: null,
    slots: [],
  });
  const [selectedSlotId, setSelectedSlotId] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneDigits, setPhoneDigits] = useState("");
  const [status, setStatus] = useState("");
  const [branches, setBranches] = useState([]);
  const [eras, setEras] = useState([]);
  const [familySize, setFamilySize] = useState(1);
  const [addToMailingList, setAddToMailingList] = useState(true);
  const [peerContactOptIn, setPeerContactOptIn] = useState(false);
  const [consent, setConsent] = useState(false);
  const [city, setCity] = useState("");
  const [stateRegion, setStateRegion] = useState("");
  const [country, setCountry] = useState("");
  const [errors, setErrors] = useState({});
  const [submitMessage, setSubmitMessage] = useState("");
  const [submitState, setSubmitState] = useState("idle");
  const [scriptReady, setScriptReady] = useState(false);
  const [widgetId, setWidgetId] = useState(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const trackedThankYouViewRef = useRef(false);
  const trackedRsvpStartRef = useRef(false);
  const defaultStatusRef = useRef("");

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setState({
        loading: true,
        error: "",
        notFound: false,
        page: null,
        event: null,
        slots: [],
      });

      const { data: page, error: pageError } = await supabase
        .from("event_pages")
        .select(PUBLIC_EVENT_PAGE_SELECT)
        .eq("slug", slug)
        .eq("published", true)
        .maybeSingle();

      if (cancelled) return;

      if (pageError) {
        setState({
          loading: false,
          error: pageError.message || "Unable to load event page.",
          notFound: false,
          page: null,
          event: null,
          slots: [],
        });
        return;
      }

      if (!page) {
        setState({
          loading: false,
          error: "",
          notFound: true,
          page: null,
          event: null,
          slots: [],
        });
        return;
      }

      const eventId = firstDefined(page, ["event_id", "eventId"]);
      let event = null;
      let slots = [];

      if (eventId) {
        const [{ data: eventData, error: eventError }, { data: slotData, error: slotError }] =
          await Promise.all([
            supabase.from("events").select(PUBLIC_EVENT_SELECT).eq("id", eventId).maybeSingle(),
            supabase
              .from("pickup_slots")
              .select("id, event_id, label, start_utc, end_utc, capacity, created_at")
              .eq("event_id", eventId)
              .order("start_utc", { ascending: true }),
          ]);

        if (cancelled) return;

        if (eventError) {
          setState({
            loading: false,
            error: eventError.message || "Unable to load event details.",
            notFound: false,
            page,
            event: null,
            slots: [],
          });
          return;
        }

        if (slotError) {
          setState({
            loading: false,
            error: slotError.message || "Unable to load pickup slots.",
            notFound: false,
            page,
            event: eventData || null,
            slots: [],
          });
          return;
        }

        event = eventData || null;
        slots = slotData || [];
      }

      setState({
        loading: false,
        error: "",
        notFound: false,
        page,
        event,
        slots: normalizeSlots(slots, firstDefined(event, ["timezone"])),
      });
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  const pageTitle = useMemo(() => {
    const title = coerceString(firstDefined(state.page, ["title", "page_title"]));
    const eventName = coerceString(firstDefined(state.event, ["name", "title"]));
    return title || eventName || titleCaseFromSlug(slug) || "Event";
  }, [slug, state.event, state.page]);

  useEffect(() => {
    document.title = `${pageTitle} | Road Home Program`;
  }, [pageTitle]);

  useEffect(() => {
    if (!selectedSlotId && state.slots.length > 0) {
      setSelectedSlotId(state.slots[0].id);
    }
  }, [selectedSlotId, state.slots]);

  const rsvpEnabled = firstDefined(state.page, ["rsvp_enabled"]) !== false;
  const pageMode = coerceString(firstDefined(state.page, ["page_mode"])) || "rsvp";
  const rsvpConfig = {
    ...DEFAULT_RSVP_CONFIG,
    ...(firstDefined(state.page, ["rsvp_config"]) || {}),
  };
  const imageConfig = {
    ...DEFAULT_IMAGE_CONFIG,
    ...(firstDefined(state.page, ["image_config"]) || {}),
  };
  const thankYouConfig = {
    ...DEFAULT_THANK_YOU_CONFIG,
    ...(firstDefined(state.page, ["thank_you_config"]) || {}),
  };
  const sunsetConfig = {
    ...DEFAULT_SUNSET_CONFIG,
    ...(firstDefined(state.page, ["sunset_config"]) || {}),
  };
  const heroCtas = getSortedEnabledCtas(firstDefined(state.page, ["cta_config"]) || [], "hero");
  const bodyCtas = getSortedEnabledCtas(firstDefined(state.page, ["cta_config"]) || [], "body");
  const sidebarCtas = getSortedEnabledCtas(firstDefined(state.page, ["cta_config"]) || [], "sidebar");
  const sunsetCtas = getSortedEnabledCtas(firstDefined(state.page, ["cta_config"]) || [], "sunset");
  const hasRsvpSlots = state.slots.length > 0;
  const eventEndDate = resolveEventEndDate(state.event, state.slots);
  const sunsetHours = Math.max(0, Number(sunsetConfig.hours_after_event_end || 0));
  const sunsetThreshold = eventEndDate
    ? new Date(eventEndDate.getTime() + sunsetHours * 60 * 60 * 1000)
    : null;
  const sunsetPassed = !!(
    sunsetConfig.enabled &&
    sunsetThreshold &&
    Date.now() >= sunsetThreshold.getTime()
  );
  const showRsvpForm = pageMode === "rsvp" && rsvpEnabled && hasRsvpSlots && !sunsetPassed;
  const showBranchField = !!rsvpConfig.show_branch;
  const showEraField = !!rsvpConfig.show_era;
  const showStatusField = !!rsvpConfig.show_rhp_client_status;
  const showPhoneField = !!rsvpConfig.show_phone;
  const showAddressField = !!rsvpConfig.show_address;
  const showPeerContactField = !!rsvpConfig.show_peer_contact_opt_in;
  const maxPartySize = Math.max(1, Number(rsvpConfig.max_party_size || 1));
  const showPartySizeField = rsvpConfig.show_party_size !== false && maxPartySize > 1;
  const defaultStatus = getConfiguredDefaultStatus(rsvpConfig.default_status);
  const resolvedStatus = showStatusField ? (status || defaultStatus) : defaultStatus;
  const showServiceFields = SERVICE_STATUSES.has(resolvedStatus);
  const showBranchSection = showBranchField && showServiceFields;
  const showEraSection = showEraField && showServiceFields;

  useEffect(() => {
    if (!state.page || !showStatusField) return;
    setStatus((current) => {
      const previousDefault = defaultStatusRef.current;
      defaultStatusRef.current = defaultStatus;
      if (!current || current === previousDefault) return defaultStatus;
      return current;
    });
  }, [defaultStatus, showStatusField, state.page]);

  useEffect(() => {
    if (showServiceFields) return;
    setBranches([]);
    setEras([]);
  }, [showServiceFields]);

  useEffect(() => {
    setFamilySize((current) => {
      const normalized = Math.max(1, Number(current) || 1);
      if (!showPartySizeField) return 1;
      return Math.min(normalized, maxPartySize);
    });
  }, [maxPartySize, showPartySizeField]);

  useEffect(() => {
    setAddToMailingList(!!rsvpConfig.mailing_list_opt_in_default_checked);
    setPeerContactOptIn(false);
  }, [rsvpConfig.mailing_list_opt_in_default_checked, state.page]);

  useEffect(() => {
    if (!showRsvpForm || !SITE_KEY) return;
    if (window.turnstile) {
      setScriptReady(true);
      return;
    }
    const existing = document.getElementById("cf-turnstile-script");
    if (existing) {
      let tries = 0;
      const interval = setInterval(() => {
        tries += 1;
        if (window.turnstile) {
          setScriptReady(true);
          clearInterval(interval);
          return;
        }
        if (tries >= 20) clearInterval(interval);
      }, 100);
      return () => clearInterval(interval);
    }
    const script = document.createElement("script");
    script.id = "cf-turnstile-script";
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.onload = () => setScriptReady(true);
    document.head.appendChild(script);
    return () => {
      script.onload = null;
    };
  }, [showRsvpForm]);

  useEffect(() => {
    if (!showRsvpForm || !scriptReady || !window.turnstile || widgetId || !SITE_KEY) return;
    if (!document.getElementById("dynamic-turnstile-container")) return;
    const id = window.turnstile.render("#dynamic-turnstile-container", {
      sitekey: SITE_KEY,
      callback: setCaptchaToken,
      "expired-callback": () => setCaptchaToken(""),
      "error-callback": () => setCaptchaToken(""),
      retry: "auto",
    });
    setWidgetId(id);
  }, [scriptReady, showRsvpForm, widgetId]);

  useEffect(() => {
    if (!captchaToken) return;
    const ev = new CustomEvent("cf-turnstile-token", {
      detail: { token: captchaToken },
    });
    document.dispatchEvent(ev);
  }, [captchaToken]);

  const getTurnstileToken = useCallback(async () => {
    if (!window.turnstile || !widgetId) return "";
    if (captchaToken) return captchaToken;
    return new Promise((resolve) => {
      const onToken = (event) => {
        resolve(event.detail.token);
        document.removeEventListener("cf-turnstile-token", onToken);
      };
      document.addEventListener("cf-turnstile-token", onToken);
      window.turnstile.execute(widgetId);
      setTimeout(() => {
        document.removeEventListener("cf-turnstile-token", onToken);
        resolve("");
      }, 3000);
    });
  }, [captchaToken, widgetId]);

  const refreshTurnstile = useCallback(() => {
    setCaptchaToken("");
    if (window.turnstile && widgetId) {
      window.turnstile.reset(widgetId);
    }
  }, [widgetId]);

  const subtitle = coerceString(firstDefined(state.page, ["subtitle", "dek", "tagline"]));
  const showBannerImage = imageConfig.show_banner_image === true || imageConfig.show_banner_image === "true";
  const bannerImage = showBannerImage ? coerceString(imageConfig.banner_image_url) : "";
  const localHeroImage = LOCAL_HERO_IMAGES_BY_SLUG[coerceString(firstDefined(state.page, ["slug"])) || slug] || "";
  const heroImage = localHeroImage || firstImageUrlExcept(bannerImage, [
    imageConfig.hero_image_url,
    firstDefined(state.page, ["hero_image_url", "hero_image", "hero_url", "image_url"]),
  ]);
  const dateTime = formatDateTime(
    firstDefined(state.event, ["date_utc", "starts_at", "start_utc"]),
    firstDefined(state.event, ["timezone"])
  );
  const rawVenueName = coerceString(
    firstDefined(state.page, ["venue_name"]) ||
      firstDefined(state.event, ["venue_name", "location_name", "name_of_venue"])
  );
  const venueAddress = coerceString(
    firstDefined(state.page, ["venue_address", "address"]) ||
      firstDefined(state.event, [
        "venue_address",
        "address",
        "address1",
        "full_address",
        "location_address",
      ])
  );
  const shortDescription = coerceString(
    firstDefined(state.page, ["short_description", "summary", "description"])
  );
  const bodyMarkdown = coerceString(firstDefined(state.page, ["body_markdown", "content_markdown"]));
  const bodyText = coerceString(firstDefined(state.page, ["body_text", "content", "body"]));
  const bodyBlocks = bodyMarkdown ? renderBodyMarkdown(bodyMarkdown) : [];
  const closedMessage = coerceString(firstDefined(state.page, ["closed_message"]));
  const thankYouTitle = coerceString(thankYouConfig.title);
  const thankYouMessage = coerceString(thankYouConfig.body || firstDefined(state.page, ["thank_you_message"]));
  const sunsetTitle = coerceString(sunsetConfig.title) || "This event has ended";
  const sunsetMessage = coerceString(sunsetConfig.message) || "Thanks for your interest. Registration is no longer available for this event.";
  const sunsetCtaLabel = coerceString(sunsetConfig.cta_label);
  const sunsetCtaUrl = coerceString(sunsetConfig.cta_url);
  const venueMapsUrl = buildMapsUrl(venueAddress);
  const eventCategory = coerceString(firstDefined(state.event, ["event_category"]));
  const webinarSignalText = [
    pageTitle,
    subtitle,
    rawVenueName,
    shortDescription,
    bodyMarkdown,
    bodyText,
    eventCategory,
  ].join(" ");
  const isWebinarEvent = /\bwebinar\b/i.test(webinarSignalText);
  const isVirtualWorkshopEvent = /\bworkshop\b/i.test(webinarSignalText) && /\bvirtual\b/i.test(webinarSignalText);
  const isCeEvent = /\bCEs?\b|continuing education|credit/i.test(webinarSignalText);
  const venueName = isWebinarEvent && /^zoom$/i.test(rawVenueName) ? "" : rawVenueName;
  const ceCreditMatch = webinarSignalText.match(/\b(\d+(?:\.\d+)?)\s*CEs?\b/i);
  const ceCreditLabel = ceCreditMatch
    ? `${ceCreditMatch[1]} ${Number(ceCreditMatch[1]) === 1 ? "CE" : "CEs"} available`
    : isCeEvent ? "Continuing education available" : "";
  const accessEventLabel = isWebinarEvent ? "Webinar" : isVirtualWorkshopEvent ? "Workshop" : "";
  const accessDetailsNote = accessEventLabel
    ? `${accessEventLabel} access details will be emailed after registration.`
    : "";
  const isSingleSlotAccessEvent = !!accessEventLabel && state.slots.length === 1;
  const primarySlotLabel = coerceString(state.slots[0]?.label);
  const eventDetailsDateTime = isSingleSlotAccessEvent && primarySlotLabel ? primarySlotLabel : dateTime;
  const heroEyebrow = "Road Home Program";
  const slotFieldLabel = isSingleSlotAccessEvent ? "Session" : "Available slot";
  const showAvailableSlotsPanel = showRsvpForm && submitState !== "success" && !isSingleSlotAccessEvent;
  const showClosedPanel = !sunsetPassed && !rsvpEnabled && (closedMessage || sunsetCtas.length > 0);
  const showSidebar = showRsvpForm || sidebarCtas.length > 0 || sunsetPassed || showClosedPanel;
  const eventPageId = firstDefined(state.page, ["id"]);
  const trackedSlug = coerceString(firstDefined(state.page, ["slug"])) || slug;
  const selectedCalendarSlot = findCalendarSlotById(state.slots, selectedSlotId);
  const calendarIsOnline = isWebinarEvent || isVirtualWorkshopEvent || /^zoom$/i.test(rawVenueName);
  const calendarLocation = calendarIsOnline
    ? "Online via Zoom"
    : [rawVenueName, venueAddress].filter(Boolean).join(", ");
  const calendarDescription = [
    "Hosted by the Road Home Program at Rush.",
  ].filter(Boolean).join("\n");
  const publicEventUrl = getCurrentPublicEventUrl();

  useEffect(() => {
    trackedThankYouViewRef.current = false;
    trackedRsvpStartRef.current = false;
  }, [eventPageId, slug]);

  useEffect(() => {
    if (!state.page) return;
    trackEventPageView({
      eventPageId,
      slug: trackedSlug,
    });
  }, [eventPageId, state.page, trackedSlug]);

  useEffect(() => {
    if (!state.page || (!sunsetPassed && !showClosedPanel)) return;
    trackEventPageEvent({
      eventPageId,
      slug: trackedSlug,
      eventType: "sunset_view",
    });
  }, [eventPageId, showClosedPanel, state.page, sunsetPassed, trackedSlug]);

  useEffect(() => {
    if (submitState !== "success" || trackedThankYouViewRef.current || !state.page) return;
    trackedThankYouViewRef.current = true;
    trackEventPageEvent({
      eventPageId,
      slug: trackedSlug,
      eventType: "thank_you_view",
    });
  }, [eventPageId, state.page, submitState, trackedSlug]);

  const toggleArrayValue = (value, currentValues, setter) => {
    setter(
      currentValues.includes(value)
        ? currentValues.filter((item) => item !== value)
        : [...currentValues, value]
    );
  };

  const handlePhoneChange = (event) => {
    const digits = event.target.value.replace(/\D/g, "").slice(0, 10);
    setPhoneDigits(digits);
    setPhone(formatPhoneDigits(digits));
    setErrors((current) => ({ ...current, phone: "" }));
  };

  const handleCtaClick = useCallback((cta) => {
    trackEventPageEvent({
      eventPageId,
      slug: trackedSlug,
      eventType: "cta_click",
      ctaId: cta?.id,
      ctaLabel: cta?.label,
    });
  }, [eventPageId, trackedSlug]);

  const handleRsvpInteraction = useCallback(() => {
    if (trackedRsvpStartRef.current || !showRsvpForm) return;
    trackedRsvpStartRef.current = true;
    trackEventPageEvent({
      eventPageId,
      slug: trackedSlug,
      eventType: "rsvp_start",
    });
  }, [eventPageId, showRsvpForm, trackedSlug]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitMessage("");
    setSubmitState("idle");

    const nextErrors = {};
    if (!selectedSlotId) nextErrors.slot_id = "Choose an available RSVP slot.";
    if (!firstName.trim()) nextErrors.first_name = "First name is required.";
    if (!lastName.trim()) nextErrors.last_name = "Last name is required.";
    const emailNorm = validateRsvpEmail(email);
    if (!emailNorm.ok) nextErrors.email = RSVP_EMAIL_VALIDATION_MESSAGE;
    if (showPhoneField && rsvpConfig.require_phone && phoneDigits.length !== 10) nextErrors.phone = "Phone is required and must be 10 digits.";
    if (showPhoneField && phoneDigits.length > 0 && phoneDigits.length !== 10) nextErrors.phone = "Phone must be 10 digits.";
    if (showStatusField && !resolvedStatus) nextErrors.status = "Status is required.";
    if (showBranchSection && rsvpConfig.require_branch && branches.length === 0) nextErrors.branch_of_service = "Choose at least one branch.";
    if (showEraSection && rsvpConfig.require_era && eras.length === 0) nextErrors.era_list = "Choose at least one era.";
    if (showAddressField && rsvpConfig.require_address) {
      if (!city.trim()) nextErrors.city = "City is required.";
      if (!stateRegion.trim()) nextErrors.state = "State / region is required.";
      if (!country.trim()) nextErrors.country = "Country is required.";
    }
    if (!consent) nextErrors.consent = "Consent is required.";

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setSubmitState("error");
      setSubmitMessage("Please review the highlighted fields and try again.");
      return;
    }

    setErrors({});
    setSubmitState("submitting");

    let token = captchaToken;
    if (!token && window.turnstile && widgetId) {
      token = await getTurnstileToken();
    }
    if (!token) {
      setSubmitState("error");
      setSubmitMessage("Human verification failed. Please try again.");
      return;
    }

    const payload = mergeAttributionIntoPayload({
      event_id: firstDefined(state.page, ["event_id", "eventId"]),
      slot_id: selectedSlotId,
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      email: emailNorm.email,
      phone: phoneDigits.length === 10 ? phone : "",
      status: resolvedStatus,
      branch_of_service: showBranchSection ? branches : [],
      era_list: showEraSection ? eras : [],
      family_size: familySize,
      add_to_mailing_list: addToMailingList,
      peer_contact_opt_in: showPeerContactField ? peerContactOptIn : false,
      interests: addToMailingList ? [`${pageTitle} updates`] : [],
      consent: true,
      city: showAddressField ? city.trim() : "",
      state: showAddressField ? stateRegion.trim() : "",
      country: showAddressField ? country.trim() : "",
      cf_turnstile_token: token,
    }, window.location, document);

    try {
      const { data, error } = await supabase.functions.invoke("reserve-rsvp", {
        body: payload,
      });

      let responseData = data && typeof data === "object" ? data : null;
      if (!responseData && error) {
        responseData = await extractResponseFromError(error);
      }

      if (error || responseData?.ok === false) {
        setSubmitState("error");
        setSubmitMessage(
          responseData?.error ||
            error?.message ||
            "Something went wrong. Please review your information and try again."
        );
        refreshTurnstile();
        return;
      }

      trackEventPageEvent({
        eventPageId,
        slug: trackedSlug,
        eventType: "rsvp_submit",
      });
      setSubmitState("success");
      setSubmitMessage(thankYouMessage || "Thanks for your RSVP. Your registration was submitted successfully.");
      setConsent(false);
      refreshTurnstile();
    } catch (err) {
      setSubmitState("error");
      setSubmitMessage(err?.message || "Something went wrong. Please try again.");
      refreshTurnstile();
    }
  };

  if (state.loading) {
    return (
      <main className="dynamic-event-page dynamic-event-page--state">
        <section className="dynamic-event-page__state-card">
          <p className="dynamic-event-page__eyebrow">Loading event</p>
          <h1 className="dynamic-event-page__state-title">Loading {pageTitle}…</h1>
          <p className="dynamic-event-page__state-copy">Fetching event details and available RSVP slots.</p>
        </section>
      </main>
    );
  }

  if (state.error) {
    return (
      <main className="dynamic-event-page dynamic-event-page--state">
        <section className="dynamic-event-page__state-card">
          <p className="dynamic-event-page__eyebrow">Event unavailable</p>
          <h1 className="dynamic-event-page__state-title">We couldn&apos;t load this event page.</h1>
          <p className="dynamic-event-page__state-copy">{state.error}</p>
          <Link className="dynamic-event-page__secondary-link" to="/signup">
            Visit the mailing list signup instead
          </Link>
        </section>
      </main>
    );
  }

  if (state.notFound) {
    return (
      <main className="dynamic-event-page dynamic-event-page--state">
        <section className="dynamic-event-page__state-card">
          <p className="dynamic-event-page__eyebrow">Not found</p>
          <h1 className="dynamic-event-page__state-title">This event page is unpublished or does not exist.</h1>
          <p className="dynamic-event-page__state-copy">
            Check the link or visit Road Home Program&apos;s current event pages.
          </p>
        </section>
      </main>
    );
  }

  return (
    <>
      <main className="dynamic-event-page">
        <section className="dynamic-event-page__hero">
          <div className="dynamic-event-page__hero-copy">
            <div className="dynamic-event-page__hero-brand">
              <img
                src={ShieldFooterIMG}
                alt=""
                aria-hidden="true"
                decoding="async"
              />
              <div>
                <p className="dynamic-event-page__eyebrow">{heroEyebrow}</p>
                <p className="dynamic-event-page__hero-brand-subtext">
                  The National Center of Excellence for Veterans and Their Families
                </p>
              </div>
            </div>
            <h1>{pageTitle}</h1>
            {subtitle ? <p className="dynamic-event-page__subtitle">{subtitle}</p> : null}
            <div className="dynamic-event-page__meta">
              {eventDetailsDateTime ? <span>{eventDetailsDateTime}</span> : null}
              {venueName ? <span>{venueName}</span> : null}
            </div>
            {renderCtaGroup(heroCtas, "hero", { onCtaClick: handleCtaClick })}
          </div>
          {heroImage ? (
            <div className="dynamic-event-page__hero-media">
              <img src={heroImage} alt={pageTitle} />
            </div>
          ) : null}
        </section>

        {bannerImage ? (
          <section className="dynamic-event-page__banner" aria-label={`${pageTitle} event banner`}>
            <img src={bannerImage} alt={`${pageTitle} event banner`} />
          </section>
        ) : null}

        {shortDescription ? (
          <section className="dynamic-event-page__event-callout" aria-label="Event date and time">
            <p>{shortDescription}</p>
          </section>
        ) : null}

        <section className="dynamic-event-page__content">
        {!sunsetPassed || !sunsetConfig.hide_event_content ? (
          <article className="dynamic-event-page__main">
            {(venueName || venueAddress || eventDetailsDateTime || ceCreditLabel || accessDetailsNote) ? (
              <section className="dynamic-event-page__panel dynamic-event-page__details-strip">
                <h2>Event Details</h2>
                <div className="dynamic-event-page__details-grid">
                  {eventDetailsDateTime ? <p><strong>Date & time</strong><span>{eventDetailsDateTime}</span></p> : null}
                  {ceCreditLabel ? <p><strong>CE</strong><span>{ceCreditLabel}</span></p> : null}
                  {venueName ? <p><strong>Venue</strong><span>{venueName}</span></p> : null}
                  {venueAddress ? (
                    <p>
                      <strong>Address</strong>
                      <span>
                        {venueMapsUrl ? (
                          <a href={venueMapsUrl} target="_blank" rel="noreferrer">
                            {venueAddress}
                          </a>
                        ) : venueAddress}
                      </span>
                    </p>
                  ) : null}
                </div>
                {accessDetailsNote ? (
                  <p className="dynamic-event-page__details-note">{accessDetailsNote}</p>
                ) : null}
              </section>
            ) : null}

            {bodyBlocks.length ? (
              <section className="dynamic-event-page__panel">
                <h2>About This Event</h2>
                <div className="dynamic-event-page__rich-text">{bodyBlocks}</div>
              </section>
            ) : bodyText ? (
              <section className="dynamic-event-page__panel">
                <h2>About This Event</h2>
                <p className="dynamic-event-page__plain-text">{bodyText}</p>
              </section>
            ) : null}

            {renderCtaGroup(bodyCtas, "body", {
              title: "Call to Action",
              className: "dynamic-event-page__panel",
              onCtaClick: handleCtaClick,
            })}
          </article>
        ) : null}

        {showSidebar ? (
          <aside className="dynamic-event-page__sidebar" id="rsvp">
            {renderCtaGroup(sidebarCtas, "sidebar", {
              title: "Quick Links",
              className: "dynamic-event-page__panel",
              onCtaClick: handleCtaClick,
            })}

            {showRsvpForm ? (
              <section className={`dynamic-event-page__panel dynamic-event-page__panel--cta${submitState === "success" ? " dynamic-event-page__panel--rsvp-success" : ""}`}>
                <p className="dynamic-event-page__eyebrow">RSVP</p>
                <h2>Reserve your spot</h2>
                {submitState !== "success" ? (
                  <>
                    <p>
                      Complete the form below to RSVP for this event. Available slots come directly from
                      the linked event schedule.
                    </p>
                    {accessDetailsNote ? <p>{accessDetailsNote}</p> : null}
                  </>
                ) : null}

                {submitState === "success" ? (
                  <div className="dynamic-event-page__submit-message dynamic-event-page__submit-message--success">
                    {thankYouTitle ? <strong>{thankYouTitle}</strong> : null}
                    {submitMessage ? <p>{submitMessage}</p> : null}
                    <AddToCalendar
                      title={pageTitle}
                      startUtc={selectedCalendarSlot?.start_utc}
                      endUtc={selectedCalendarSlot?.end_utc}
                      timezone={firstDefined(state.event, ["timezone"])}
                      location={calendarLocation}
                      description={calendarDescription}
                      publicEventUrl={publicEventUrl}
                      onProviderSelect={handleCtaClick}
                    />
                  </div>
                ) : (
                  <form
                    className="dynamic-event-page__form"
                    onSubmit={handleSubmit}
                    onFocusCapture={handleRsvpInteraction}
                    onChange={handleRsvpInteraction}
                    noValidate
                  >
                    {isSingleSlotAccessEvent ? (
                      <div className="dynamic-event-page__field">
                        <span>{slotFieldLabel}</span>
                        <div className="dynamic-event-page__session-display">
                          {state.slots[0]?.label || "Session details will be confirmed after registration."}
                        </div>
                        {errors.slot_id ? <span className="dynamic-event-page__field-error">{errors.slot_id}</span> : null}
                      </div>
                    ) : (
                      <label className="dynamic-event-page__field">
                        <span>{slotFieldLabel}</span>
                        <select
                          value={selectedSlotId}
                          onChange={(e) => {
                            setSelectedSlotId(e.target.value);
                            setErrors((current) => ({ ...current, slot_id: "" }));
                          }}
                          aria-invalid={errors.slot_id ? "true" : undefined}
                        >
                          <option value="">Select a slot</option>
                          {state.slots.map((slot) => (
                            <option key={slot.id} value={slot.id}>
                              {slot.label}
                            </option>
                          ))}
                        </select>
                        {errors.slot_id ? <span className="dynamic-event-page__field-error">{errors.slot_id}</span> : null}
                      </label>
                    )}

                    <div className="dynamic-event-page__row">
                      <label className="dynamic-event-page__field">
                        <span>First name</span>
                        <input
                          required
                          value={firstName}
                          onChange={(e) => {
                            setFirstName(e.target.value);
                            setErrors((current) => ({ ...current, first_name: "" }));
                          }}
                          aria-invalid={errors.first_name ? "true" : undefined}
                        />
                        {errors.first_name ? <span className="dynamic-event-page__field-error">{errors.first_name}</span> : null}
                      </label>
                      <label className="dynamic-event-page__field">
                        <span>Last name</span>
                        <input
                          required
                          value={lastName}
                          onChange={(e) => {
                            setLastName(e.target.value);
                            setErrors((current) => ({ ...current, last_name: "" }));
                          }}
                          aria-invalid={errors.last_name ? "true" : undefined}
                        />
                        {errors.last_name ? <span className="dynamic-event-page__field-error">{errors.last_name}</span> : null}
                      </label>
                    </div>

                    <div className="dynamic-event-page__row">
                      <label className="dynamic-event-page__field">
                        <span>Email</span>
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => {
                            setEmail(e.target.value);
                            setErrors((current) => ({ ...current, email: "" }));
                          }}
                          aria-invalid={errors.email ? "true" : undefined}
                        />
                        {errors.email ? <span className="dynamic-event-page__field-error">{errors.email}</span> : null}
                      </label>
                      {showPhoneField ? (
                        <label className="dynamic-event-page__field">
                          <span>Phone</span>
                          <input
                            value={phone}
                            onChange={handlePhoneChange}
                            placeholder="(555) 123-4567"
                            inputMode="numeric"
                            aria-invalid={errors.phone ? "true" : undefined}
                          />
                          {errors.phone ? <span className="dynamic-event-page__field-error">{errors.phone}</span> : null}
                        </label>
                      ) : null}
                    </div>

                    {showStatusField || showPartySizeField ? (
                      <div className={`dynamic-event-page__row${showStatusField !== showPartySizeField ? " dynamic-event-page__row--single" : ""}`}>
                        {showStatusField ? (
                          <label className="dynamic-event-page__field">
                            <span>Status</span>
                            <select
                              value={resolvedStatus}
                              onChange={(e) => {
                                setStatus(e.target.value);
                                setErrors((current) => ({ ...current, status: "" }));
                              }}
                            >
                              {STATUS_OPTIONS.map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </label>
                        ) : null}

                        {showPartySizeField ? (
                          <label className="dynamic-event-page__field">
                            <span>Party size</span>
                            <select
                              value={familySize}
                              onChange={(e) => setFamilySize(Number(e.target.value) || 1)}
                            >
                              {Array.from({ length: maxPartySize }, (_, index) => index + 1).map((size) => (
                                <option key={size} value={size}>
                                  {size}
                                </option>
                              ))}
                            </select>
                          </label>
                        ) : null}
                      </div>
                    ) : null}

                    {showBranchSection || showEraSection ? (
                      <>
                        {showBranchSection ? (
                          <div className="dynamic-event-page__field">
                            <span>Branch of service</span>
                            <div className="dynamic-event-page__chip-grid">
                              {BRANCH_OPTIONS.map((option) => {
                                const selected = branches.includes(option);
                                return (
                                  <button
                                    key={option}
                                    type="button"
                                    className={`dynamic-event-page__chip${selected ? " dynamic-event-page__chip--selected" : ""}`}
                                    aria-pressed={selected}
                                    onClick={() => toggleArrayValue(option, branches, setBranches)}
                                  >
                                    <span aria-hidden="true" className="dynamic-event-page__chip-check">
                                      {selected ? "✓" : ""}
                                    </span>
                                    <span>{option}</span>
                                  </button>
                                );
                              })}
                            </div>
                            {errors.branch_of_service ? (
                              <span className="dynamic-event-page__field-error">{errors.branch_of_service}</span>
                            ) : null}
                          </div>
                        ) : null}

                        {showEraSection ? (
                          <div className="dynamic-event-page__field">
                            <span>Era</span>
                            <div className="dynamic-event-page__chip-grid">
                              {ERA_OPTIONS.map((option) => {
                                const selected = eras.includes(option);
                                return (
                                  <button
                                    key={option}
                                    type="button"
                                    className={`dynamic-event-page__chip${selected ? " dynamic-event-page__chip--selected" : ""}`}
                                    aria-pressed={selected}
                                    onClick={() => toggleArrayValue(option, eras, setEras)}
                                  >
                                    <span aria-hidden="true" className="dynamic-event-page__chip-check">
                                      {selected ? "✓" : ""}
                                    </span>
                                    <span>{option}</span>
                                  </button>
                                );
                              })}
                            </div>
                            {errors.era_list ? (
                              <span className="dynamic-event-page__field-error">{errors.era_list}</span>
                            ) : null}
                          </div>
                        ) : null}
                      </>
                    ) : null}

                    {showAddressField ? (
                      <div className="dynamic-event-page__address-fields">
                        <label className="dynamic-event-page__field">
                          <span>City</span>
                          <input
                            name="city"
                            autoComplete="address-level2"
                            required={!!rsvpConfig.require_address}
                            value={city}
                            onChange={(e) => {
                              setCity(e.target.value);
                              setErrors((current) => ({ ...current, city: "" }));
                            }}
                            aria-invalid={errors.city ? "true" : undefined}
                          />
                          {errors.city ? <span className="dynamic-event-page__field-error">{errors.city}</span> : null}
                        </label>
                        <label className="dynamic-event-page__field">
                          <span>State / Region</span>
                          <input
                            name="state"
                            autoComplete="address-level1"
                            required={!!rsvpConfig.require_address}
                            value={stateRegion}
                            onChange={(e) => {
                              setStateRegion(e.target.value);
                              setErrors((current) => ({ ...current, state: "" }));
                            }}
                            aria-invalid={errors.state ? "true" : undefined}
                          />
                          {errors.state ? <span className="dynamic-event-page__field-error">{errors.state}</span> : null}
                        </label>
                        <label className="dynamic-event-page__field">
                          <span>Country</span>
                          <input
                            name="country"
                            autoComplete="country-name"
                            required={!!rsvpConfig.require_address}
                            value={country}
                            onChange={(e) => {
                              setCountry(e.target.value);
                              setErrors((current) => ({ ...current, country: "" }));
                            }}
                            aria-invalid={errors.country ? "true" : undefined}
                          />
                          {errors.country ? <span className="dynamic-event-page__field-error">{errors.country}</span> : null}
                        </label>
                      </div>
                    ) : null}

                    <label className="dynamic-event-page__check">
                      <input
                        type="checkbox"
                        checked={addToMailingList}
                        onChange={(e) => setAddToMailingList(e.target.checked)}
                      />
                      <span>Add me to the Road Home Program mailing list for event updates.</span>
                    </label>

                    {showPeerContactField ? (
                      <label className="dynamic-event-page__check">
                        <input
                          type="checkbox"
                          checked={peerContactOptIn}
                          onChange={(e) => setPeerContactOptIn(e.target.checked)}
                        />
                        <span>I’m open to peer contact follow-up related to this event.</span>
                      </label>
                    ) : null}

                    <label className="dynamic-event-page__check">
                      <input
                        type="checkbox"
                        checked={consent}
                        onChange={(e) => {
                          setConsent(e.target.checked);
                          setErrors((current) => ({ ...current, consent: "" }));
                        }}
                      />
                      <span>I agree to submit this RSVP and receive {accessEventLabel ? accessEventLabel.toLowerCase() : "event"} registration details by email.</span>
                    </label>
                    {errors.consent ? <span className="dynamic-event-page__field-error">{errors.consent}</span> : null}

                    {submitState === "error" && submitMessage ? (
                      <div className="dynamic-event-page__submit-message">{submitMessage}</div>
                    ) : null}

                    <div id="dynamic-turnstile-container" />

                    <button className="dynamic-event-page__cta" type="submit" disabled={submitState === "submitting"}>
                      {submitState === "submitting" ? "Submitting..." : "Submit RSVP"}
                    </button>
                  </form>
                )}
              </section>
            ) : null}

            {sunsetPassed ? (
              <section className="dynamic-event-page__panel dynamic-event-page__panel--sunset">
                <p className="dynamic-event-page__eyebrow">Event Ended</p>
                <h2>{sunsetTitle}</h2>
                <p>{sunsetMessage}</p>
                {sunsetCtaLabel && sunsetCtaUrl ? (
                  sunsetCtaUrl.startsWith("/") ? (
                    <Link
                      className="dynamic-event-page__cta-link"
                      to={sunsetCtaUrl}
                      onClick={() => handleCtaClick({
                        id: "sunset-primary-cta",
                        label: sunsetCtaLabel,
                      })}
                    >
                      {sunsetCtaLabel}
                    </Link>
                  ) : (
                    <a
                      className="dynamic-event-page__cta-link"
                      href={sunsetCtaUrl}
                      onClick={() => handleCtaClick({
                        id: "sunset-primary-cta",
                        label: sunsetCtaLabel,
                      })}
                      {...getCtaLinkProps(sunsetCtaUrl)}
                    >
                      {sunsetCtaLabel}
                    </a>
                  )
                ) : null}
                {renderCtaGroup(sunsetCtas, "sunset", { onCtaClick: handleCtaClick })}
              </section>
            ) : null}

            {showClosedPanel ? (
              <section className="dynamic-event-page__panel dynamic-event-page__panel--closed">
                <p className="dynamic-event-page__eyebrow">Registration Closed</p>
                <h2>Updates for this event</h2>
                {closedMessage ? <p>{closedMessage}</p> : null}
                {renderCtaGroup(sunsetCtas, "sunset", { onCtaClick: handleCtaClick })}
              </section>
            ) : null}

            {showAvailableSlotsPanel ? (
              <section className="dynamic-event-page__panel">
                <h2>Available Slots</h2>
                {state.slots.length ? (
                  <ul className="dynamic-event-page__slot-list">
                    {state.slots.map((slot) => (
                      <li key={slot.id}>{slot.label}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="dynamic-event-page__empty-slots">
                    No pickup slots are linked to this event yet.
                  </p>
                )}
              </section>
            ) : null}
          </aside>
        ) : null}
        </section>
      </main>
      <RHPSiteFooter />
    </>
  );
}

export default DynamicEventPage;
